import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SearchForm from "../../components/SearchForm";
import { Link } from "react-router-dom";
import CardSimpleClient from "../../components/CardSimpleClient";
import CardProfessional from "../../components/CardProfessional";
import ChartColumn from "../../components/ChartColumn";
import { getToken, URL } from "../../utils";
import axios from "axios";
import ChartFullBg from "../../components/ChartFullBg";

export default function Clients() {
  const [clientsData, setClientsData] = useState({
    simpleClients: [],
    professionals: [],
    stats: {},
    data: [0, 0],
    statisticData: {
      week: { data: [], labels: [] },
      month: { data: [], labels: [] },
      year: { data: [], labels: [] },
    },
  });
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState({
    simpleClients: [],
    professionals: [],
  });
  const token = getToken();

  const categories = [
    "Particuliers",
    "Professionnels",
    "Total",
    "Nouveaux Particuliers",
    "Nouveaux Professionnels",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  // Effet pour filtrer les données quand le terme de recherche change
  useEffect(() => {
    filterData();
  }, [searchTerm, clientsData]);

  // Fonction de filtrage des données
  const filterData = () => {
    if (!searchTerm.trim()) {
      setFilteredData({
        simpleClients: clientsData.simpleClients,
        professionals: clientsData.professionals,
      });
      return;
    }

    const searchLower = searchTerm.toLowerCase();

    const filteredSimpleClients = clientsData.simpleClients.filter(
      (client) =>
        client.nom?.toLowerCase().includes(searchLower) ||
        client.prenom?.toLowerCase().includes(searchLower) ||
        client.contact1?.toLowerCase().includes(searchLower) ||
        client.adresse?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower)
    );

    const filteredProfessionals = clientsData.professionals.filter(
      (client) =>
        client.nom_entreprise?.toLowerCase().includes(searchLower) ||
        client.secteur_activite?.toLowerCase().includes(searchLower) ||
        client.contact?.toLowerCase().includes(searchLower) ||
        client.adresse?.toLowerCase().includes(searchLower) ||
        client.email?.toLowerCase().includes(searchLower)
    );

    setFilteredData({
      simpleClients: filteredSimpleClients,
      professionals: filteredProfessionals,
    });
  };

  // Fonction de gestion de la recherche
  const handleSearch = (value) => {
    setSearchTerm(value);
  };

  const fetchData = async () => {
    try {
      // Test de la fonction de formatage de date
      // Regrouper les appels API avec Promise.all
      const [
        statsResponse,
        simpleClientsResponse,
        professionalsResponse,
        weekDataResponse,
        monthDataResponse,
        yearDataResponse,
      ] = await Promise.all([
        axios.get(URL + "Clients/clients/statistiques_nbre", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/clients-particuliers-list", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/clients-professionnels-list", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/chiffres-affaire-clients_w", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/chiffres-affaire-clients_m", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
        axios.get(URL + "Clients/chiffres-affaire-clients_y", {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      // Pour les données hebdomadaires, regroupons par jour avec format jour + mois
      const weekTemp = { data: [], labels: [] };
      const weekDataGrouped = {};

      if (weekDataResponse.data.ventes_par_periode) {
        weekDataResponse.data.ventes_par_periode.forEach((item) => {
          const date = new Date(item.date_sortie__date);
          const formattedDate = date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
          });

          if (!weekDataGrouped[formattedDate]) {
            weekDataGrouped[formattedDate] = 0;
          }
          weekDataGrouped[formattedDate] += item.total || 0;
        });

        // Conversion du dictionnaire en tableaux ordonnés
        Object.keys(weekDataGrouped).forEach((date) => {
          weekTemp.labels.push(date);
          weekTemp.data.push(weekDataGrouped[date]);
        });
      }

      // Pour les données mensuelles, regroupons par jour avec format jour + mois + année
      const monthTemp = { data: [], labels: [] };
      const monthDataGrouped = {};

      if (monthDataResponse.data.ventes_par_periode) {
        monthDataResponse.data.ventes_par_periode.forEach((item) => {
          const date = new Date(item.date_sortie__date);
          const formattedDate = date.toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "long",
            year: "numeric",
          });

          if (!monthDataGrouped[formattedDate]) {
            monthDataGrouped[formattedDate] = 0;
          }
          monthDataGrouped[formattedDate] += item.total || 0;
        });

        // Conversion du dictionnaire en tableaux ordonnés avec tri chronologique
        const dateKeys = Object.keys(monthDataGrouped);
        const sortedDates = dateKeys
          .map((dateStr) => {
            const parts = dateStr.split(" ");
            const day = parseInt(parts[0]);
            const month = [
              "janvier",
              "février",
              "mars",
              "avril",
              "mai",
              "juin",
              "juillet",
              "août",
              "septembre",
              "octobre",
              "novembre",
              "décembre",
            ].indexOf(parts[1]);
            const year = parseInt(parts[2]);
            return {
              dateStr,
              dateObj: new Date(year, month, day),
              value: monthDataGrouped[dateStr],
            };
          })
          .sort((a, b) => a.dateObj - b.dateObj);

        sortedDates.forEach((item) => {
          monthTemp.labels.push(item.dateStr);
          monthTemp.data.push(item.value);
        });
      }

      // Pour les données annuelles, regroupons par mois
      const yearTemp = { data: [], labels: [] };
      const yearDataGrouped = {};

      if (yearDataResponse.data.ventes_par_periode) {
        yearDataResponse.data.ventes_par_periode.forEach((item) => {
          // Pour les données annuelles, formater en mois
          const monthName = item.mois
            ? new Date(item.mois).toLocaleString("fr-FR", { month: "long" })
            : new Date(item.date_sortie__date).toLocaleString("fr-FR", {
                month: "long",
              });

          if (!yearDataGrouped[monthName]) {
            yearDataGrouped[monthName] = 0;
          }
          yearDataGrouped[monthName] += item.total || 0;
        });

        // Définir l'ordre des mois en français
        const monthOrder = [
          "janvier",
          "février",
          "mars",
          "avril",
          "mai",
          "juin",
          "juillet",
          "août",
          "septembre",
          "octobre",
          "novembre",
          "décembre",
        ];

        // Conversion du dictionnaire en tableaux ordonnés selon l'ordre des mois
        monthOrder.forEach((month) => {
          if (yearDataGrouped[month] !== undefined) {
            yearTemp.labels.push(month);
            yearTemp.data.push(yearDataGrouped[month]);
          }
        });
      }

      const statsData = {
        week: weekTemp,
        month: monthTemp,
        year: yearTemp,
      };

      const simpleClients =
        simpleClientsResponse.data?.data ?? simpleClientsResponse.data ?? [];
      const professionals =
        professionalsResponse.data?.data ?? professionalsResponse.data ?? [];

      // Mettre à jour l'état en une seule fois
      setClientsData({
        simpleClients,
        professionals,
        stats: statsResponse.data,
        data: [
          statsResponse.data.nb_particuliers,
          statsResponse.data.nb_professionnels,
          statsResponse.data.nombres_total,
          statsResponse.data.nouveaux_particuliers,
          statsResponse.data.nouveaux_professionnels,
        ],
        statisticData: statsData,
      });

      // Afficher un toast de succès
      // toast.success("Données chargées avec succès !");
    } catch (error) {
      toast.error("Erreur lors du chargement des données.");
    } finally {
      setLoading(false); // Désactiver le chargement
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="flex flex-col items-center">
          {/* Animation */}
          <div className="loader"></div>
          <div className="w-10 h-10 border-2 border-t-2 border-gray-200 rounded-full animate-spin my-4"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ToastContainer />
      <div className="shadow-md bg-white p-4 flex justify-between items-center sticky top-16 z-40">
        <SearchForm
          placeholder="Rechercher un client"
          onSearch={handleSearch}
        />
      </div>

      {/* Section Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 bg-gray-50 m-4">
        {/* <div className="p-2 grid grid-cols-1 gap-2">
          <div className="">
            <Datepicker />
          </div>
          <div className="text-2xl font-semibold flex justify-between items-center p-2">
            <h2>Statistiques</h2>
          </div>
          {clientsData.data && (
            <div className="bg-white shadow-md rounded-md">
              <ChartColumn
                categories={categories}
                data={clientsData.data}
                showLabels={false}
                name={"Nombre de clients"}
              />
            </div>
          )}
        </div> */}
        <div className="bg-white space-y-2 shadow-md rounded-md p-2 ">
          <h2 className="text-2xl font-bold px-4">
            Statistiques sur le nombre clients
          </h2>
          <div className="h-96 grid">
            {clientsData.data && (
              <ChartColumn
                categories={categories}
                data={clientsData.data}
                showLabels={false}
                name={"Nombre de clients"}
              />
            )}
          </div>
        </div>
        {/* Show Charts following the selected period */}
        <div className="grid gap-4 mx-auto w-full p-4 bg-white shadow-md rounded-md">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-semibold">
              {
                {
                  week: "Historique des achats de la semaine",
                  month: "Historique des achats du mois",
                  year: "Historique des achats de l'année",
                }[selectedPeriod] // Récupère le titre en fonction de la période sélectionnée
              }
            </h2>

            <select
              className="w-max p-2 border-2 rounded-md bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500"
              name="selectedPeriod"
              id="selectedPeriod"
              value={selectedPeriod}
              onChange={(e) => {
                setSelectedPeriod(e.target.value);
              }}
            >
              <option value="week">Semaine</option>
              <option value="month">Mois</option>
              <option value="year">Année</option>
            </select>
          </div>

          <div className="flex items-center max-h-96">
            {
              {
                week: (
                  <ChartFullBg
                    key={"week-" + Math.random()}
                    data={clientsData.statisticData.week.data}
                    dataLabels={clientsData.statisticData.week.labels}
                    colors={["#f97316"]}
                    title="Variation du chiffre d'affaires durant la semaine"
                    period="week"
                    tooltipSuffix=" FCFA"
                  />
                ),
                month: (
                  <ChartFullBg
                    key={"month-" + Math.random()}
                    data={clientsData.statisticData.month.data}
                    dataLabels={clientsData.statisticData.month.labels}
                    colors={["#fbbf24"]}
                    title="Variation du chiffre d'affaires durant le mois"
                    period="month"
                    tooltipSuffix=" FCFA"
                  />
                ),
                year: (
                  <ChartFullBg
                    key={"year-" + Math.random()}
                    data={clientsData.statisticData.year.data}
                    dataLabels={clientsData.statisticData.year.labels}
                    colors={["#84cc16"]}
                    title="Variation du chiffre d'affaires durant l'année"
                    period="year"
                    tooltipSuffix=" FCFA"
                  />
                ),
              }[selectedPeriod]
            }
          </div>
        </div>
        {/* <div className="bg-white space-y-2 shadow-md rounded-md p-2">
          <h2 className="text-2xl font-bold px-4">Les achats des clients</h2>
          <Chart />
        </div> */}
      </div>

      {/* Section Particuliers */}
      <div className="p-4 pt-0">
        <div className="text-2xl font-semibold flex justify-between items-center p-2">
          <h2>Particuliers ({filteredData.simpleClients.length})</h2>
          <Link
            to="/clients/particuliers"
            className="flex items-center justify-center p-2 bg-white shadow-md rounded-md"
          >
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </Link>
        </div>
        <div className="flex items-stretch bg-white gap-4 p-4 overflow-x-auto">
          {filteredData.simpleClients.map((item) => (
            <div className="shrink-0 w-full sm:w-72" key={item.id}>
              <CardSimpleClient item={item} />
            </div>
          ))}
        </div>
      </div>

      {/* Section Professionnels */}
      <div className="p-4 pt-0">
        <div className="text-2xl font-semibold flex justify-between items-center p-2">
          <h2>Professionnels ({filteredData.professionals.length})</h2>
          <Link
            to="/clients/professionnels"
            className="flex items-center justify-center p-2 bg-white shadow-md rounded-md"
          >
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m9 5 7 7-7 7"
              />
            </svg>
          </Link>
        </div>
        <div className="flex items-stretch bg-white gap-4 p-4 overflow-x-auto">
          {filteredData.professionals.map((item) => (
            <div className="shrink-0 w-full sm:w-72" key={item.id}>
              <CardProfessional item={item} />
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
